import { ChangeDetectionStrategy, Component, ElementRef, output, input, inject } from '@angular/core';
/**
 * An internal class that represents the data corresponding to a single calendar cell.
 *
 * @docs-private
 */
export class ApCalendarCell {
    constructor(
        public value: number,
        public displayValue: string,
        public ariaLabel: string,
        public enabled: boolean
    ) {}
}

/**
 * An internal component used to display calendar data in a table.
 *
 * @docs-private
 */
@Component({
    selector: '[ap-calendar-body]',
    templateUrl: 'calendar-body.component.html',
    styleUrls: ['./calendar-body.component.scss'],
    host: {
        class: 'ap-calendar-body',
        role: 'grid',
        'attr.aria-readonly': 'true' // eslint-disable-line @typescript-eslint/naming-convention
    },
    exportAs: 'apCalendarBody',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApCalendarBodyComponent {
    private readonly elementRef = inject(ElementRef);

    /** The label for the table. (e.g. "Jan 2017"). */
    readonly label = input<string>(undefined);

    /** The cells to display in the table. */
    readonly rows = input<ApCalendarCell[][]>(undefined);

    /** The value in the table that corresponds to today. */
    readonly todayValue = input<number>(undefined);

    /** The value in the table that is currently selected. */
    readonly selectedValue = input<number>(undefined);

    /** The minimum number of free cells needed to fit the label in the first row. */
    readonly labelMinRequiredCells = input<number>(undefined);

    /** The number of columns in the table. */
    readonly numCols = input(7);

    /** Whether to allow selection of disabled cells. */
    readonly allowDisabledSelection = input(false);

    /** The cell number of the active cell in the table. */
    readonly activeCell = input(0);

    /**
     * The aspect ratio (width / height) to use for the cells in the table. This aspect ratio will be
     * maintained even as the table resizes.
     */
    readonly cellAspectRatio = input(1);

    /** Emits when a new value is selected. */
    readonly selectedValueChange = output<number>();

    /** The number of blank cells to put at the beginning for the first row. */
    get _firstRowOffset(): number {
        const rows = this.rows();
        return rows && rows.length && rows[0].length ? this.numCols() - rows[0].length : 0;
    }

    _cellClicked(cell: ApCalendarCell): void {
        if (!this.allowDisabledSelection() && !cell.enabled) {
            return;
        }
        this.selectedValueChange.emit(cell.value);
    }

    _isActiveCell(rowIndex: number, colIndex: number): boolean {
        let cellNumber = rowIndex * this.numCols() + colIndex;

        // Account for the fact that the first row may not have as many cells.
        if (rowIndex) {
            cellNumber -= this._firstRowOffset;
        }

        return cellNumber === this.activeCell();
    }

    /** Focuses the active cell after the microtask queue is empty. */
    _focusActiveCell() {
        requestAnimationFrame(() => {
            const activeElement = this.elementRef.nativeElement.querySelector('.ap-calendar-body--active');
            if (activeElement) {
                activeElement.parentElement.focus();
            }
        });
    }
}
